/**
 * Page Rapport E-commerce — Rapportage comptable des commandes en ligne
 */

import { useState, useEffect } from 'react';
import { startOfMonth, endOfMonth, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import DateRangePicker, { DateRange } from '../components/analytics/DateRangePicker';
import { ecomReportApi, EcomReportPreview, EcomReport } from '../api/ecom-report.api';
import { ouvrirDocumentPrive } from '../utils/documentPrive';

type Tab = 'preview' | 'history';

const formatCents = (cents: number): string => {
  return (cents / 100).toFixed(2).replace('.', ',') + ' €';
};

export default function EcomReportPage() {
  const [activeTab, setActiveTab] = useState<Tab>('preview');
  const [dateRange, setDateRange] = useState<DateRange>({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date()),
  });
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<EcomReportPreview | null>(null);
  const [reports, setReports] = useState<EcomReport[]>([]);
  const [reportsTotal, setReportsTotal] = useState(0);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Charger le preview quand la période change
  useEffect(() => {
    if (activeTab === 'preview') {
      loadPreview();
    }
  }, [dateRange, activeTab]);

  // Charger l'historique
  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory();
    }
  }, [activeTab]);

  const loadPreview = async () => {
    setLoading(true);
    setError(null);
    try {
      const fromStr = format(dateRange.from, 'yyyy-MM-dd');
      const toStr = format(dateRange.to, 'yyyy-MM-dd');
      const data = await ecomReportApi.preview(fromStr, toStr);
      setPreview(data);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    setLoading(true);
    try {
      const data = await ecomReportApi.list(30, 0);
      setReports(data.reports);
      setReportsTotal(data.total);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReport = async () => {
    setCreating(true);
    setError(null);
    setSuccess(null);
    try {
      const fromStr = format(dateRange.from, 'yyyy-MM-dd');
      const toStr = format(dateRange.to, 'yyyy-MM-dd');
      const report = await ecomReportApi.create({ from: fromStr, to: toStr });
      setSuccess(`Rapport ${report.report_number} créé avec succès`);
      // Rafraîchir le preview pour afficher le rapport existant
      loadPreview();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleExportCsv = () => {
    const fromStr = format(dateRange.from, 'yyyy-MM-dd');
    const toStr = format(dateRange.to, 'yyyy-MM-dd');
    const url = ecomReportApi.getExportCsvUrl(fromStr, toStr);
    window.open(url, '_blank');
  };

  const handleExportLinesCsv = () => {
    const fromStr = format(dateRange.from, 'yyyy-MM-dd');
    const toStr = format(dateRange.to, 'yyyy-MM-dd');
    const url = ecomReportApi.getExportLinesCsvUrl(fromStr, toStr);
    window.open(url, '_blank');
  };

  const tabs: { key: Tab; label: string }[] = [
    { key: 'preview', label: 'Aperçu' },
    { key: 'history', label: 'Historique' },
  ];

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Rapport E-commerce</h1>
          <p className="text-sm text-gray-500 mt-1">Rapportage comptable des commandes en ligne</p>
        </div>
      </div>

      {/* Date Range Picker */}
      <DateRangePicker value={dateRange} onChange={setDateRange} />

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex -mb-px space-x-8">
          {tabs.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* Notifications */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-700 text-sm">
          {success}
        </div>
      )}

      {/* Contenu */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : activeTab === 'preview' && preview ? (
        <PreviewTab
          preview={preview}
          creating={creating}
          onCreateReport={handleCreateReport}
          onExportCsv={handleExportCsv}
          onExportLinesCsv={handleExportLinesCsv}
        />
      ) : activeTab === 'history' ? (
        <HistoryTab reports={reports} total={reportsTotal} />
      ) : null}
    </div>
  );
}

// ========== Preview Tab ==========

function PreviewTab({
  preview,
  creating,
  onCreateReport,
  onExportCsv,
  onExportLinesCsv,
}: {
  preview: EcomReportPreview;
  creating: boolean;
  onCreateReport: () => void;
  onExportCsv: () => void;
  onExportLinesCsv: () => void;
}) {
  return (
    <div className="space-y-6">
      {/* Rapport existant warning */}
      {preview.existing_report && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-700 text-sm">
          Un rapport existe déjà pour cette période : <strong>{preview.existing_report.report_number}</strong>
          {preview.existing_report.pdf_url && (
            <button
              type="button"
              onClick={() => void ouvrirDocumentPrive(preview.existing_report!.pdf_url!)}
              className="ml-2 text-blue-600 hover:underline"
            >
              Télécharger le PDF
            </button>
          )}
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <KpiCard label="CA TTC" value={formatCents(preview.total_ca_ttc_cents)} color="blue" />
        <KpiCard label="CA HT" value={formatCents(preview.total_ca_ht_cents)} color="green" />
        <KpiCard label="TVA collectée" value={formatCents(preview.total_tax_cents)} color="purple" />
        <KpiCard label="Commandes" value={String(preview.completed_orders_count)} color="blue" />
        <KpiCard label="Panier moyen" value={formatCents(preview.average_basket_cents)} color="green" />
      </div>

      {/* Sections détaillées */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* TVA */}
        <Section title="Ventilation TVA">
          {preview.vat_breakdown.rate_0.base_cents > 0 && (
            <Row label="Exonéré (0%) - Base" value={formatCents(preview.vat_breakdown.rate_0.base_cents)} />
          )}
          <Row label="TVA 6% - Base HT" value={formatCents(preview.vat_breakdown.rate_6.base_cents)} />
          <Row label="TVA 6% - Montant" value={formatCents(preview.vat_breakdown.rate_6.amount_cents)} />
          <Row label="TVA 21% - Base HT" value={formatCents(preview.vat_breakdown.rate_21.base_cents)} />
          <Row label="TVA 21% - Montant" value={formatCents(preview.vat_breakdown.rate_21.amount_cents)} />
        </Section>

        {/* Paiements */}
        <Section title="Moyens de paiement">
          <Row label="Wallet client" value={formatCents(preview.payment_methods.wallet_cents)} />
          <Row label="Carte bancaire (Stripe)" value={formatCents(preview.payment_methods.card_cents)} />
          {preview.payment_methods.cash_cents > 0 && (
            <Row label="Espèces" value={formatCents(preview.payment_methods.cash_cents)} />
          )}
        </Section>

        {/* Annulations & Remboursements */}
        <Section title="Annulations & Remboursements">
          <Row
            label="Commandes annulées"
            value={`${preview.cancellations.count} (${formatCents(preview.cancellations.total_cents)})`}
          />
          <Row
            label="Remboursements"
            value={`${preview.refunds.count} (${formatCents(preview.refunds.total_cents)})`}
          />
          {preview.refunds.by_type?.map((r) => (
            <Row
              key={r.refund_type}
              label={`  ${r.refund_type}`}
              value={`${r.count} (${formatCents(r.total_cents)})`}
              sub
            />
          ))}
        </Section>

        {/* Livraisons */}
        <Section title="Livraisons">
          <Row label="Livraisons" value={String(preview.delivery_breakdown.delivery_count)} />
          <Row label="Retraits magasin" value={String(preview.delivery_breakdown.pickup_count)} />
          <Row label="Frais de livraison" value={formatCents(preview.delivery_fees_cents)} />
          {preview.discount_total_cents > 0 && (
            <Row label="Remises totales" value={formatCents(preview.discount_total_cents)} />
          )}
        </Section>
      </div>

      {/* Commandes par statut */}
      {preview.orders_by_status.length > 0 && (
        <Section title="Commandes par statut">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {preview.orders_by_status.map((s) => (
              <div key={s.status} className="text-center p-3 bg-gray-50 rounded-lg">
                <StatusBadge status={s.status} />
                <div className="text-lg font-semibold mt-1">{s.count}</div>
                <div className="text-xs text-gray-500">{formatCents(s.total_cents)}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Factures Enfact */}
      {preview.enfact_invoices.count > 0 && (
        <Section title={`Factures Enfact (${preview.enfact_invoices.count})`}>
          <Row label="Total HT" value={formatCents(preview.enfact_invoices.total_ht_cents)} />
          <Row label="Total TTC" value={formatCents(preview.enfact_invoices.total_ttc_cents)} bold />
          {preview.enfact_invoices.invoices.map((inv) => (
            <Row
              key={inv.id}
              label={`${inv.enfact_invoice_number}${inv.customer_name ? ` - ${inv.customer_name}` : ''}`}
              value={formatCents(inv.total_ttc_cents)}
              sub
            />
          ))}
        </Section>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3 pt-4 border-t">
        <button
          onClick={onCreateReport}
          disabled={creating}
          className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {creating ? 'Génération...' : 'Générer le rapport PDF'}
        </button>
        <button
          onClick={onExportCsv}
          className="px-6 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
        >
          Exporter commandes (CSV)
        </button>
        <button
          onClick={onExportLinesCsv}
          className="px-6 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
        >
          Exporter lignes (CSV)
        </button>
      </div>
    </div>
  );
}

// ========== History Tab ==========

function HistoryTab({ reports, total }: { reports: EcomReport[]; total: number }) {
  if (reports.length === 0) {
    return (
      <div className="text-center py-20 text-gray-400">
        Aucun rapport e-commerce généré
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="p-4 border-b bg-gray-50">
        <p className="text-sm text-gray-500">{total} rapport(s) au total</p>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b">
          <tr>
            <th className="text-left px-4 py-3 font-medium text-gray-600">N° Rapport</th>
            <th className="text-left px-4 py-3 font-medium text-gray-600">Période</th>
            <th className="text-right px-4 py-3 font-medium text-gray-600">Commandes</th>
            <th className="text-right px-4 py-3 font-medium text-gray-600">CA TTC</th>
            <th className="text-right px-4 py-3 font-medium text-gray-600">TVA</th>
            <th className="text-center px-4 py-3 font-medium text-gray-600">PDF</th>
            <th className="text-left px-4 py-3 font-medium text-gray-600">Créé le</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {reports.map((r) => (
            <tr key={r.id} className="hover:bg-gray-50">
              <td className="px-4 py-3 font-mono text-blue-600">{r.report_number}</td>
              <td className="px-4 py-3 text-gray-600">
                {format(new Date(r.period_start), 'dd/MM/yyyy', { locale: fr })} —{' '}
                {format(new Date(r.period_end), 'dd/MM/yyyy', { locale: fr })}
              </td>
              <td className="px-4 py-3 text-right">{r.completed_orders_count}</td>
              <td className="px-4 py-3 text-right font-medium">{formatCents(r.total_ca_ttc_cents)}</td>
              <td className="px-4 py-3 text-right text-gray-600">{formatCents(r.total_tax_cents)}</td>
              <td className="px-4 py-3 text-center">
                {r.pdf_url ? (
                  <button
                    type="button"
                    onClick={() => void ouvrirDocumentPrive(r.pdf_url!)}
                    className="text-blue-600 hover:underline"
                  >
                    PDF
                  </button>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </td>
              <td className="px-4 py-3 text-gray-500">
                {format(new Date(r.created_at), 'dd/MM/yyyy HH:mm', { locale: fr })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ========== Composants utilitaires ==========

function KpiCard({ label, value, color }: { label: string; value: string; color: string }) {
  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-50 border-blue-200',
    green: 'bg-green-50 border-green-200',
    purple: 'bg-purple-50 border-purple-200',
    red: 'bg-red-50 border-red-200',
    orange: 'bg-orange-50 border-orange-200',
  };

  return (
    <div className={`rounded-lg border p-4 ${colorClasses[color] || colorClasses.blue}`}>
      <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
      <div className="text-xl font-bold mt-1 text-gray-900">{value}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-lg shadow p-5">
      <h3 className="font-semibold text-gray-800 mb-3">{title}</h3>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Row({ label, value, bold, sub }: { label: string; value: string; bold?: boolean; sub?: boolean }) {
  return (
    <div className={`flex items-center justify-between py-1 ${sub ? 'pl-4 text-gray-500' : ''}`}>
      <span className={`text-sm ${bold ? 'font-semibold' : 'text-gray-600'}`}>{label}</span>
      <span className={`text-sm ${bold ? 'font-semibold' : ''}`}>{value}</span>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-blue-100 text-blue-800',
    preparing: 'bg-purple-100 text-purple-800',
    ready: 'bg-green-100 text-green-800',
    completed: 'bg-gray-100 text-gray-800',
    cancelled: 'bg-red-100 text-red-800',
  };
  const labels: Record<string, string> = {
    pending: 'En attente',
    confirmed: 'Confirmées',
    preparing: 'En préparation',
    ready: 'Prêtes',
    completed: 'Complétées',
    cancelled: 'Annulées',
  };

  return (
    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${styles[status] || 'bg-gray-100 text-gray-600'}`}>
      {labels[status] || status}
    </span>
  );
}
